import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import "./style.css";
import routes from "./routes.tsx";
import ThemeProvider from "./shared/providers/ThemeProvider";
import QueryProvider from "./shared/providers/QueryProvider";
import { Toaster } from "./shared/components/ui/sonner";
import { setAuthTokenResolver, setUnauthorizedHandler } from "./shared/api";
import {
  clearStoredAccessToken,
  getStoredAccessToken,
} from "./shared/utils/auth";
import { clearMembershipSelection } from "./shared/utils/membership";
import { clearPhoneVerificationState } from "./shared/utils/phoneVerification";
import { clearPendingEmailVerification } from "./features/auth/utils/email-verification";
import { Sentry } from "./shared/config/sentry.client";
import { installGlobalErrorReporting } from "./shared/monitoring/error-reporting";

const AppErrorFallback: Sentry.FallbackRender = ({ error, resetError }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-main-dark px-6 text-center text-white">
    <h1 className="text-2xl font-semibold">Something went wrong</h1>
    <p className="max-w-md text-sm text-white/70">
      An unexpected error occurred. Our team has been notified. You can try
      reloading the page or returning to the previous step.
    </p>
    {error ? (
      <pre className="max-w-md overflow-auto rounded-lg bg-black/40 px-4 py-3 text-left text-xs text-white/70">
        {error instanceof Error ? error.message : String(error)}
      </pre>
    ) : null}
    <div className="flex flex-col gap-3 sm:flex-row">
      <button
        type="button"
        className="rounded-full bg-white px-6 py-2 text-sm font-medium text-main-dark"
        onClick={() => window.location.assign("/")}>
        Go to Dashboard
      </button>
      <button
        type="button"
        className="rounded-full border border-white/40 px-6 py-2 text-sm font-medium text-white hover:bg-white/10"
        onClick={resetError}>
        Try Again
      </button>
    </div>
  </div>
);

setAuthTokenResolver(() => getStoredAccessToken());
installGlobalErrorReporting(Sentry);

let lastUnauthorizedRedirectPath: string | null = null;

setUnauthorizedHandler(() => {
  if (typeof window === "undefined") {
    return;
  }

  clearStoredAccessToken();
  clearMembershipSelection();
  clearPhoneVerificationState();
  clearPendingEmailVerification();

  const { pathname } = window.location;
  const isAuthRoute =
    pathname.startsWith("/sign-in") ||
    pathname.startsWith("/sign-up") ||
    pathname.startsWith("/forgot-password") ||
    pathname.startsWith("/reset-password");

  if (isAuthRoute) {
    lastUnauthorizedRedirectPath = null;
    return;
  }

  if (lastUnauthorizedRedirectPath === pathname) {
    return;
  }

  lastUnauthorizedRedirectPath = pathname;
  window.location.replace("/sign-in");
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryProvider>
        <Sentry.ErrorBoundary fallback={AppErrorFallback}>
          <RouterProvider router={routes} />
        </Sentry.ErrorBoundary>
        <Toaster richColors position="top-center" />
      </QueryProvider>
    </ThemeProvider>
  </StrictMode>
);
